import React from 'react';
import { hot } from 'react-hot-loader/root';
import { Provider, useSelector } from 'react-redux';
import { ThemeProvider } from 'styled-components';
import { BrowserRouter as Router, Switch, Route } from 'react-router-dom';
import { ConfigProvider, message } from 'antd';
import store from './redux/store';
import './static/css/style.css';
import config from './config/config';
import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import AuthPage from './pages/AuthPage';
import StudentAuth from './pages/StudentAuth';
import ClientAuth from './pages/ClientAuth';
import ClientAuthPage from './pages/auth/ClientAuthPage';
import StudentAuthPage from './pages/auth/StudentAuthPage';
import AdminAuthPage from './pages/auth/AdminAuthPage';
import InstituteAuthPage from './pages/auth/InstituteAuthPage';
import MentorAuthPage from './pages/auth/MentorAuthPage';

import LandingPage from './pages/LandingPage';
import CallbackPage from './pages/CallbackPage';
import Admin from './routes/admin';
import ClientProfileCompletion from './container/clientTab/components/ClientProfileCompletion';
import StudentProfileCompletion from './container/clientTab/components/StudentProfileCompletion';
import 'antd/dist/antd.less';

const { theme } = config;

message.config({ top: 24, maxCount: 1, duration: 2, zIndex: 1200 });

const ProviderConfig = () => {
    const { rtl, topMenu, darkMode } = useSelector( state => ( {
        darkMode: state.ChangeLayoutMode.data,
        rtl: state.ChangeLayoutMode.rtlData,
        topMenu: state.ChangeLayoutMode.topMenu,
    } ), (prev, next) => 
        prev.darkMode === next.darkMode && 
        prev.rtl === next.rtl && 
        prev.topMenu === next.topMenu
    );

    return (
        <ConfigProvider direction={ rtl ? 'rtl' : 'ltr' }>
            <ThemeProvider theme={ { ...theme, rtl, topMenu, darkMode } }>
                <Router basename={ process.env.PUBLIC_URL }>
                    <AuthProvider>
                        <Switch>
                            {/* Self-onboarding route for users without profiles */}
                            <Route path="/self-onboarding/client/profile">
                                <ProtectedRoute requiredRoles={['*']}>
                                    <ClientProfileCompletion />
                                </ProtectedRoute>
                            </Route>

                            <Route path="/self-onboarding/student/profile">
                                <ProtectedRoute requiredRoles={['*']}>
                                    <StudentProfileCompletion />
                                </ProtectedRoute>
                            </Route>
                            
                            {/* Protected admin routes */}
                            <Route path="/admin">
                                <ProtectedRoute requiredRoles={['admin', 'super_admin', 'manager', '*']}>
                                    <Admin />
                                </ProtectedRoute>
                            </Route>
                            
                            {/* OIDC callback route */}
                            <Route path="/callback" component={CallbackPage} />
                            
                            {/* Role-specific auth routes */}
                            <Route path="/student-auth" component={StudentAuth} />
                            <Route path="/client-auth" component={ClientAuth} />
                            <Route path="/auth/client" component={ClientAuthPage} />
                            <Route path="/auth/student" component={StudentAuthPage} />
                            <Route path="/auth/admin" component={AdminAuthPage} />
                            <Route path="/auth/institute" component={InstituteAuthPage} />
                            <Route path="/auth/mentor" component={MentorAuthPage} />
                            
                            {/* Public auth route */}
                            <Route path="/auth" component={AuthPage} />
                            
                            {/* Landing page - redirect based on auth status */}
                            <Route path="/" component={LandingPage} />
                        </Switch>
                    </AuthProvider>
                </Router>
            </ThemeProvider>
        </ConfigProvider>
    );
}

function App() {
    return (
        <Provider store={ store }>
            <ProviderConfig />
        </Provider>
    );
}

export default hot( App );
